/** Cube storage intake (plan §4.3): a counting task — rows, columns, back, base. */
import { z } from 'zod';
import type { Design } from '../../domain/schemas';
import { mm } from '../../domain/units';
import type { FamilyIntake } from '../types';
import type { DesignFromFitInput } from '../registry-types';
import { createCube, defaultCubeParams, type CubeParams } from './design';
import { baseFromFit, BaseFitShape, FrontsFitShape, FRONTS_GUIDE, SideFitShape, sidesFromFit } from '../fit-common';

export const CUBE_CLASSIFIER = `**cube-storage** — a closed box divided into a lattice of EQUAL, roughly square cells (rows × columns), the verticals lining up straight from bottom to top and the horizontals straight from side to side, so every line runs through the whole lattice both ways. Inner and outer boards are often the same thickness. Includes 2 × 2 to 5 × 5 cube units (Kallax style), 1 × n cube benches and TV units, cube room dividers and cube units on feet, even with boxes or a few doors in some cells.
Rules it out: cells of clearly different sizes or bays with their own shelf heights (grid); verticals that change position from row to row (offset grid); no continuous outer sides (staggered); doors or drawers alone do not rule it out (they are fitted as fronts); metal frames.`;

export const CubeFitParamsSchema = z.object({
  rows: z.int().min(0).describe('Rows of cells, counted top to bottom.'),
  cols: z.int().min(0).describe('Columns of cells, counted left to right.'),
  back: z.enum(['none', 'full', 'per-cell', 'unknown']).describe('full: a back panel behind every cell; per-cell: backs in only some cells; none: open (wall visible through the cells).'),
  ...BaseFitShape,
  ...FrontsFitShape,
  ...SideFitShape,
});
export type CubeFitParams = z.input<typeof CubeFitParamsSchema>;

export const CUBE_PARAMS_GUIDE = `Count the rows and the columns of cells exactly (a 4 × 4 unit has rows 4 and cols 4; a bench of three cubes side by side has rows 1 and cols 3). Say whether there is a back behind the cells, what the bottom stands on (look at the floor line: small feet or a recessed plinth under the bottom board) and whether the outer sides are solid or slatted. ${FRONTS_GUIDE}`;

export const CUBE_INTAKE: FamilyIntake<CubeFitParams> = { classifier: CUBE_CLASSIFIER, paramsSchema: CubeFitParamsSchema, paramsGuide: CUBE_PARAMS_GUIDE };

export function checkCubeParams(p: CubeFitParams): string | null {
  if (p.rows < 1 || p.cols < 1) return `${p.rows} × ${p.cols} cells`;
  return null;
}

export function cubeParamsFromFit(f: CubeFitParams | null, H: number, D = mm(350)): CubeParams {
  const base = defaultCubeParams();
  if (!f) return base;
  const b = baseFromFit(f, H, D);
  return { ...base, rows: Math.max(1, Math.min(6, f.rows)), cols: Math.max(1, Math.min(6, f.cols)), back: f.back === 'full' ? 'overlay' : 'none', plinth: b.plinth ?? 0, ...b, ...sidesFromFit(f, D) };
}

export function cubeDesignFromFit(input: DesignFromFitInput<CubeFitParams>): Design {
  const d = createCube();
  return { ...d, W: input.W, H: input.H, D: input.D, stockProfileId: input.stock?.id ?? d.stockProfileId, params: cubeParamsFromFit(input.params, input.H, input.D) as unknown as Record<string, unknown> };
}

export const cubeFitSummary = (p: CubeFitParams): string => `${p.cols} × ${p.rows} cubes`;
